import { supabase } from '../supabaseClient';

interface AIConfig {
  provider: 'openai' | 'azure';
  api_key: string;
  azure_endpoint?: string;
  azure_deployment?: string;
  model?: string;
}

export const aiService = {
  async createConnection(config: AIConfig) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const connectionName = config.provider === 'azure' ? 'Azure OpenAI' : 'OpenAI';

    const { data, error } = await supabase
      .from('connections')
      .insert({
        user_id: user.id,
        connection_type: 'ai',
        connection_name: connectionName,
        status: 'active',
        config: {
          provider: config.provider,
          azure_endpoint: config.azure_endpoint,
          deployment_name: config.azure_deployment,
          model: config.model,
        },
        // Store API key encrypted (handled by RLS/database)
        api_key: config.api_key,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateConnection(connectionId: string, config: AIConfig) {
    const connectionName = config.provider === 'azure' ? 'Azure OpenAI' : 'OpenAI';
    
    const { data, error } = await supabase
      .from('connections')
      .update({
        connection_name: connectionName,
        config: {
          provider: config.provider,
          azure_endpoint: config.azure_endpoint,
          deployment_name: config.azure_deployment,
          model: config.model,
        },
        api_key: config.api_key,
        status: 'active',
        last_sync_at: new Date().toISOString(),
      })
      .eq('id', connectionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteConnection(connectionId: string) {
    const { error } = await supabase
      .from('connections')
      .delete()
      .eq('id', connectionId);

    if (error) throw error;
  },

  async generateExecutiveSummary(params: {
    clientName: string;
    projectName: string;
    observations: string;
    goals: string;
    opportunities: string;
  }): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${SUPABASE_URL}/functions/v1/ai-generate-summary`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate summary');
    }

    const { summary } = await response.json();
    return summary;
  },
};
